import { MonitoringPlanType } from 'mbs-ui-shared@models/MonitoringPlanType';
import { QueryPagingParams, QuerySortParams } from 'mbs-ui-shared@models/Paging';
import { SidePanelUriParams } from 'mbs-ui-shared@models/SidePanelUriParams';
import { SidepanelRouteType } from 'mbs-ui-shared@models/sidepanel';

export enum BackupHistoryPlanResultsEnum {
  Deleted = 1,
  Failed,
  MissedRun,
  Running,
  Success,
  Warning,
  Scheduled
}

export enum BackupHistoryPeriodShowType {
  Last30Day,
  Month
}

export type BaseBackupHistoryFilters = {
  issuesOnly?: boolean;
  companies?: string[];
  search?: string;
};

export type BackupHistoryRemoteFilters = {
  month?: number;
  year?: number;
  issuesOnly?: boolean;
  search?: string;
  companyIds?: string[];
  fullBackup?: boolean;
  statusType?: string; // for Exporting CSV
};

export type BackupHistoryLocalFilters = BaseBackupHistoryFilters & {
  showLatestPlanResult: number;
  backupFullType?: number;
  periodMonth: number;
  periodYear: number;
  statusType?: string; // for Exporting CSV
};

export type RemoteBackupHistoryParams = Partial<QuerySortParams> & Partial<QueryPagingParams> & BackupHistoryRemoteFilters;
export type LocalBackupHistoryParams = Partial<QuerySortParams> & Partial<QueryPagingParams> & BackupHistoryLocalFilters;

export type BackupHistorySidePanelUriParams = SidePanelUriParams & {
  sidepanel?: SidepanelRouteType;
  date?: string;
  plan?: string;
};

export type BackupHistoryUriParams = BackupHistorySidePanelUriParams & {
  period?: number;
  search?: string;
  companies?: string[];
  backupFullType?: number;
  issuesOnly?: boolean;
  showLatestPlanResult?: number;
};

export type SidepanelBackupHistoryData = {
  computerHid: string;
  date: string;
  plan: string;
};

export enum BackupStatuses {
  None = -1,
  Unknown = 0,
  Success = 1,
  Failed = 2,
  Interrupted = 3,
  UnexpectedlyClosed = 4,
  Running = 5,
  Warning = 6,
  PlanCreated = 101,
  PlanDeleted = 102,
  Scheduled = 103,
  MissingRun = 104,
  Overdue = 'Overdue'
}

export enum RestoreVerificationStatus {
  None = 0,
  Success,
  Warning,
  Executing
}

export enum RunTypeMode {
  Unknown = 0,
  Incremental = 1,
  SyntheticFull = 2,
  Full = 3,
  LogBackup = 4,
  DiffBackup = 5
}

export enum FormatType {
  CBF = 0,
  NBF = 1
}

export enum GFSStorageType {
  NotAssigned = 0,
  Weekly = 1,
  Monthly = 2,
  Yearly = 3
}

export type SessionStatus = {
  backupStatus: BackupStatuses;
  restoreVerificationStatus: RestoreVerificationStatus;
  fullBackup?: boolean;
};

export type BackupHistoryRemoteDayInfo = {
  date: {
    year: number;
    month: number;
    day: number;
  };
  worstSession: SessionStatus;
  bestSession: SessionStatus;
  lastSession: SessionStatus;
};

export type BackupHistoryRemote = {
  computers: {
    hid: string;
    name?: string;
    displayName?: string;
    lastSuccessBackupUtcDate?: string; // DateTime

    user?: {
      companyName?: string;
      email?: string;
    };

    plans: {
      id: string;
      name?: string;
      type?: MonitoringPlanType;

      lastBackupStatus?: BackupStatuses;
      lastBackupUtcDate?: string; // DateTime
      lastSuccessBackupUtcDate?: string; // DateTime

      days: BackupHistoryRemoteDayInfo[];
    }[];
  }[];

  totalComputers: number;
  totalPlans: number;
};

export type BackupHistoryDayInfoDetail = {
  backupStatus?: BackupHistoryDayStatusInfo;
  verificationStatus?: BackupHistoryDayStatusInfo;
  fullBackup?: boolean;
};

export type BackupHistoryDayStatusInfo = {
  icon?: string;
  tooltip: string;
};

export type BackupHistoryDayInfo = {
  worstStatus?: BackupHistoryDayInfoDetail[];
  bestStatus?: BackupHistoryDayInfoDetail[];
  lastStatus?: BackupHistoryDayInfoDetail[];
};

export type BackupHistory = {
  id: string;
  computer: {
    hid: string;
    name?: string;
    displayName?: string;
    dateLastSuccessBackup?: string; // DateTime

    user?: {
      companyName?: string;
      email?: string;
    };
  };

  plan?: {
    id: string;
    isVerificationStatus?: boolean;
    name?: string;
    lastBackupStatus: BackupStatuses;
    lastBackupStatusText: string;
    dateLastBackup: string; // DateTime
    dateLastSuccessBackup: string; // DateTime
    type?: MonitoringPlanType;
    days: BackupHistoryDayInfo;
  };

  isCardHeader?: boolean; // Used for grouping
};

export type BackupHistoryDay = {
  year: number;
  month: number;
  day: number;
};

export type BackupHistorySessionFilter = {
  planId: string;
  hid: string;
  request?: BackupHistoryDay & {
    prev?: boolean;
    next?: boolean;
  };
  date: BackupHistoryDay;
};

export type BackupHistorySessionFilesFilter = BackupHistorySessionFilter & {
  offset: number;
};

export type BackupHistoryDaySessions = {
  sessions?: BackupHistorySession[];
  prevDay?: BackupHistoryDay;
  nextDay?: BackupHistoryDay;
};

export type BackupHistorySession = {
  utcStart?: string; // DateTime
  utcEnd?: string; // DateTime
  failedFiles: number;
  copiedFiles: number;
  totalCopied: number;
  backupMessage?: string;
  restoreVerificationMessage?: string;
  detailedReportLink?: string;
  status: SessionStatus;
};

export type BackupHistorySessionsExchange = {
  currentDate: string;
  plan: string;
  minDate: string;
  maxDate: string;
};
